// The two realtime custom elements (ADR-0013).
//
//   <turbo-mercure-stream-source topic hub-url>  renders Turbo Stream HTML
//   <mercure-data-source topic hub-url>          re-dispatches JSON data frames
//
// Both names and both attributes are unchanged from the Mercure/SSE versions,
// so no view template churns — only the VALUE of `hub-url`, which is now a
// WebSocket path rather than a hub URL.
//
// Everything about connecting, retrying, keeping alive and tearing down lives
// in `socket.ts` and is shared. What is left here is the only thing the two
// elements genuinely differ on: what to do with a frame. Each ignores the other
// kind, because both kinds ride the same socket.
//
// Authentication is not visible in this file, deliberately. A same-origin
// WebSocket handshake is an ordinary HTTP request, so `_autoboost_session`
// rides along automatically; the server resolves it and DERIVES the permitted
// topic. The topic in the query string is a request, not a grant.

import type { RealtimeFrame } from "./frame";
import { type Subscription, subscribe } from "./socket";

/**
 * Shared base: read the two attributes, subscribe on connect, release on
 * disconnect. Subclasses implement `onFrame` only.
 *
 * The attributes are read in `connectedCallback` and REMEMBERED, because
 * `disconnectedCallback` must release the same (hub, topic) pair it took —
 * and by then the element may already have been detached from a DOM whose
 * attributes Turbo has since rewritten.
 */
abstract class RealtimeSourceElement extends HTMLElement {
  #subscription: Subscription | null = null;

  /** Handle one frame. Called only for frames of this element's kind. */
  protected abstract onFrame(frame: RealtimeFrame): void;

  /** Prefix for this element's log lines. */
  protected abstract get label(): string;

  connectedCallback(): void {
    const topic = this.getAttribute("topic");
    const hubUrl = this.getAttribute("hub-url");
    if (topic === null || topic === "" || hubUrl === null || hubUrl === "") {
      console.warn(`${this.label} missing topic or hub-url`, { topic, hubUrl });
      return;
    }
    console.log(`${this.label} connect topic=`, topic);
    this.#subscription = subscribe(hubUrl, topic, (frame) => this.onFrame(frame));
  }

  disconnectedCallback(): void {
    this.#subscription?.release();
    this.#subscription = null;
  }
}

/** `<turbo-mercure-stream-source>` — hands Turbo Stream HTML to Turbo. */
export class TurboMercureStreamSource extends RealtimeSourceElement {
  protected get label(): string {
    return "[turbo-mercure-stream-source]";
  }

  protected onFrame(frame: RealtimeFrame): void {
    if (frame.kind !== "turbo-stream") return;
    if (frame.html === "") return;

    // `window.Turbo` is set by the Turbo import in application.js. It is read
    // through a narrowing check rather than a global declaration because a
    // frame arriving before Turbo has initialised is a real ordering case, not
    // an impossible one — the socket opens as soon as the element connects.
    const turbo: unknown = (window as { Turbo?: unknown }).Turbo;
    const render =
      typeof turbo === "object" && turbo !== null
        ? (turbo as { renderStreamMessage?: unknown }).renderStreamMessage
        : undefined;
    if (typeof render !== "function") {
      console.warn("[turbo-mercure] Turbo.renderStreamMessage unavailable; dropping message");
      return;
    }
    render.call(turbo, frame.html);
  }
}

/** `<mercure-data-source>` — re-dispatches the payload as `mercure:data`. */
export class MercureDataSource extends RealtimeSourceElement {
  protected get label(): string {
    return "[mercure-data-source]";
  }

  protected onFrame(frame: RealtimeFrame): void {
    if (frame.kind !== "data") return;
    // `detail` is the data frame itself (`_model`/`_id`/`_action`/`data`) —
    // unchanged from the SSE version, so every existing listener still matches.
    document.dispatchEvent(new CustomEvent("mercure:data", { detail: frame.payload }));
  }
}

/**
 * Register both elements. Idempotent: `customElements.define` throws on a
 * duplicate name, and this module is imported from one entry point but may be
 * evaluated twice under a bundler that does not dedupe.
 */
export function defineRealtimeElements(): void {
  if (customElements.get("turbo-mercure-stream-source") === undefined) {
    customElements.define("turbo-mercure-stream-source", TurboMercureStreamSource);
  }
  if (customElements.get("mercure-data-source") === undefined) {
    customElements.define("mercure-data-source", MercureDataSource);
  }
}
